/*

** ScrollStepListener.js **

Watches provided $el for rolls of the mouse wheel.
Even works with touch scrolling (which is finicky!).

Will only trigger with each new roll, given delay is complete or manually completed.

--

Dependencies:

- jQuery

Todo:

--

Last update 03.20.17 by David Robbins

*/

class ScrollStepListener {

	constructor(el, options){

		this.el = el;
		this.$el = $(this.el);

		// default options

		this.defaults = {
			$touchEl: null,
			autoInit: true,
			minTrendingDeltas: 2, // number of inputs in the same direction minimum
			triggerSensitivity: 1.1, // 1.1 recommended
			triggerDelay: 500, // min time between triggers
			initialDelay: 500, // time before listening
			onTriggerCallback: null,
			onScrollCompleteCallback: null,
		};

		// private vars

		this._initialized = false;
		this._initialDelayTimeout = null;
		this._lastDelta = 0;
		this._deltaTrend = [];
		this._deltaTimeout = null;
		this._deltaInterval = null;
		this._dragStartY = 0;
		this._triggerSensitivity = 1;

		// public vars

		this.options = this._fillMissingOptions(options);
		this.isTriggering = false;
		this.isScrollEnabled = true;
		this.isTriggerDelayComplete = false;

		//

		this.$touchEl = this.options.$touchEl && this.options.$touchEl.length ? this.options.$touchEl : this.$el;

		//

		this._triggerSensitivity = window.utils.DOMUtils.isIE() ? 1 : this.options.triggerSensitivity;
		this._initialDelayTimeout = window.setTimeout($.proxy(this._onInitialTimeoutComplete, this), this.options.initialDelay);
	}

	// run init (or wait for user) -------------------------------------------------------------  /

	_onInitialTimeoutComplete(){
		this.isTriggerDelayComplete = true;
		if(this.options.autoInit === true){ this.init(); }
	}

	/* private methods */

	// add / remove listeners ----------------------------------------------------------  /

	_addListeners(){

		// mouse

		this.$el.mousewheel($.proxy(this._onMouseWheel, this));

		// touch

		let options = {preventDefault:true, threshold:1};

		this.$touchEl.hammer(options)
			.on('panup pandown panmove panstart', $.proxy(this._onDragStart, this))
			.on('panend', $.proxy(this._onDragEnd, this));
	}

	_removeListeners(){

		// mouse

		this.$el.unmousewheel($.proxy(this._onMouseWheel, this));

		// touch

		this.$touchEl.hammer()
			.off('panup pandown panmove panstart', $.proxy(this._onDragStart, this))
			.off('panend', $.proxy(this._onDragEnd, this));

		// intervals

		if(this._deltaTimeout){ window.clearTimeout(this._deltaTimeout); }
		if(this._deltaInterval){ window.clearInterval(this._deltaInterval); }

		this._deltaTimeout = null;
		this._deltaInterval = null;
	}

	_onDragStart(e){

		if(!this._dragStartY && parseInt(e.gesture.deltaY)){
			this._dragStartY = e.gesture.deltaY;
		}

		e.preventDefault();
	}

	_onDragEnd(e){

		let deltaY = e.gesture.deltaY;

		if(this.isScrollEnabled && Math.abs(deltaY - this._dragStartY) > 10){

			if(deltaY - this._dragStartY > 0){ this._trigger(-1); }
			else { this._trigger(1); }

			this._dragStartY = 0;
		}

		e.preventDefault();
	}

	// mouse wheel ---------------------------------------------------------------------  /

	_onMouseWheel(e){

		if(this.isScrollEnabled){

			let delta = e.deltaY;
			this._deltaTrend.push(delta);

			// convert mousewheel delta into steps

			let stepDelta = 0;
			if(delta > 0){ stepDelta = -1; }
			else if(delta < 0){ stepDelta = 1; }

			if(Math.abs(delta) >= Math.abs(this._lastDelta) * this._triggerSensitivity && this._getDeltaTrendStatus() === true){
				if(stepDelta !== 0){
					this._trigger(stepDelta);
				}
			}

			this._lastDelta = delta;

			if(this._deltaInterval){ window.clearInterval(this._deltaInterval); }

			this._deltaInterval = window.setInterval(() => {

				if(this._lastDelta == delta){

					this._lastDelta = 0;
					window.clearInterval(this._deltaInterval);

					if(this.options.onScrollCompleteCallback){
						this.options.onScrollCompleteCallback.apply(null, [stepDelta]);
					}
				}
			}, 200);
		}
	}

	// validate that x number of previous deltas were going the same direction -----------------  /

	_getDeltaTrendStatus(){

		let minTrendingDeltas = this.options.minTrendingDeltas;

		if(this._deltaTrend.length < minTrendingDeltas){ return false; }
		else {

			let firstDeltaPositive = null;

			for(let i=0; i<minTrendingDeltas; i++){
				let isPositive = (this._deltaTrend[this._deltaTrend.length-(i+1)] > 0);
				if(i == 0){ firstDeltaPositive = isPositive; }
				else if(firstDeltaPositive != isPositive){ return false; }
			}
		}

		return true;
	}

	// trigger (when mouse scroll has exceeded triggerDistance) --------------------------------  /

	_trigger(delta){

		if(this.isTriggerDelayComplete){

			this.isTriggerDelayComplete = false;
			this._deltaTrend = [];

			if(this._deltaTimeout){
				window.clearTimeout(this._deltaTimeout);
				this._deltaTimeout = null;
			}

			// fire callback

			if(this.options.onTriggerCallback){ this.options.onTriggerCallback.apply(null, [delta]); }

			// dispatch event

			this.$el.triggerHandler('scroll', delta);

			// reset timer

			if(this.options.triggerDelay > 0){
				this._deltaTimeout = window.setTimeout($.proxy(this.setTriggerDelayComplete, this), this.options.triggerDelay);
			}
		}
	}

	// fill any missing options with defaults

	_fillMissingOptions(options){

		let modifiedOptions = $.extend({}, options);

		for(let key in modifiedOptions){
			let val = (modifiedOptions[key] === 'undefined') ? null : modifiedOptions[key];
			if(val == undefined || val == null){ modifiedOptions[key] = this.defaults[key]; }
		}

		return $.extend({}, this.defaults, modifiedOptions);;
	}

	// manual trigger delay complete

	setTriggerDelayComplete(){
		this.isTriggerDelayComplete = true;
	}

	// public methods --------------------------------------------------------------------------  /

	init(){

		if(!this._initialized){
			this._initialized = true;
			this._addListeners();
		}
	}

	setNewOptions(newOptions){

		newOptions = $.extend({}, this.options);

		for(let key in newOptions){
			newOptions[key] = newOptions[key];
		}

		this.options = newOptions;
	}

	destroy(){
		this._removeListeners();
		this.options.onTriggerCallback = null;
	}
}

module.exports = ScrollStepListener;
